import { useState } from 'react';
import { useLogs } from '@/services/queries';
import type { LogListQuery } from '@/types';
import { Badge } from '@/shared/ui/badge';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ErrorState, Pagination } from '@/components/common';
import { formatDateTime } from '@/shared/lib/format';
import { humanise } from '@/lib/format';

const PAGE_SIZE = 50;

/** Sentinel for "no filter". An empty string is not a valid Select value. */
const ANY = 'any';

const LEVELS = ['debug', 'info', 'warning', 'error'] as const;
const COMPONENTS = ['nudent', 'nucad'] as const;

/**
 * The log viewer.
 *
 * This is the largest table in the system, so it is filtered by default rather
 * than offering an unfiltered read as the starting state - the server caps the
 * page size too, but a UI whose default action is "fetch everything" is a UI
 * that will eventually be pointed at a server that forgot to.
 *
 * Two timestamps are shown because they answer different questions and can
 * disagree by a lot: `occurredAt` is the client's clock on a machine that may
 * have been offline for a week, `ingestedAt` is ours. Ordering follows ours.
 */
export function LogTable() {
  const [level, setLevel] = useState<string>(ANY);
  const [component, setComponent] = useState<string>(ANY);
  const [offset, setOffset] = useState(0);

  const query: LogListQuery = {
    limit: PAGE_SIZE,
    offset,
    ...(level !== ANY ? { logLevel: level as LogListQuery['logLevel'] } : {}),
    ...(component !== ANY ? { appComponent: component as LogListQuery['appComponent'] } : {}),
  };

  const logs = useLogs(query);

  /** Any filter change invalidates the page number it was taken from. */
  const changeFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setOffset(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={level} onValueChange={changeFilter(setLevel)}>
          <SelectTrigger className="w-40" aria-label="Filter by level">
            <SelectValue placeholder="Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All levels</SelectItem>
            {LEVELS.map((value) => (
              <SelectItem key={value} value={value}>
                {humanise(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={component} onValueChange={changeFilter(setComponent)}>
          <SelectTrigger className="w-40" aria-label="Filter by component">
            <SelectValue placeholder="Component" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All components</SelectItem>
            {COMPONENTS.map((value) => (
              <SelectItem key={value} value={value}>
                {value === 'nucad' ? 'NuCAD' : 'NuDent'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {logs.isFetching && !logs.isPending && (
          <Badge variant="outline" className="ml-auto">
            Refreshing
          </Badge>
        )}
      </div>

      {logs.isError && (
        <ErrorState
          title="Could not load logs"
          description={logs.error.message}
          action={
            <Button
              variant="outline"
              onClick={() => {
                void logs.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      )}

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Level</TableHead>
              <TableHead className="hidden sm:table-cell w-28">Component</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="hidden lg:table-cell w-48">Occurred</TableHead>
              <TableHead className="w-48">Ingested</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.isPending &&
              Array.from({ length: 8 }, (_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={5}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {logs.data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  No log lines match these filters.
                </TableCell>
              </TableRow>
            )}

            {logs.data?.data.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>
                  <StatusPill status={entry.logLevel} minWidth={72} />
                </TableCell>
                <TableCell className="hidden sm:table-cell text-muted-foreground">
                  {entry.appComponent === 'nucad' ? 'NuCAD' : 'NuDent'}
                </TableCell>
                {/*
                  Wrapped rather than truncated. A log line is read to find out
                  what happened, and an ellipsis usually lands exactly on the
                  part somebody needed.
                */}
                <TableCell className="max-w-xl break-words whitespace-pre-wrap">
                  {entry.message}
                </TableCell>
                <TableCell className="hidden lg:table-cell text-muted-foreground tabular-nums">
                  {entry.occurredAt ? formatDateTime(entry.occurredAt) : '—'}
                </TableCell>
                <TableCell className="text-muted-foreground tabular-nums">
                  {formatDateTime(entry.ingestedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>

      {logs.data && (
        <Pagination
          offset={offset}
          pageSize={PAGE_SIZE}
          count={logs.data.data.length}
          total={logs.data.meta.total}
          hasMore={logs.data.meta.hasMore}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
}
